import { useQuery } from '@tanstack/react-query';
import { Bell, Check, ChevronsUpDown, LayoutGrid, Plus } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem, useSidebar } from '@/components/ui/sidebar';
import { api } from '@/lib/api';
import { APP_NAME } from '@/lib/brand';
import type { App, Page } from '@/lib/types';
import { useCurrentApp } from './use-current-app';

/** The application's logo, or its first letter when it has none. */
export const AppMark = ({ name, logo, className = 'size-8' }: { name: string; logo?: string | null | undefined; className?: string }) => (
  logo
    ? <img src={logo} alt="" className={`shrink-0 rounded-lg border bg-background object-contain ${className}`} />
    : <span className={`grid shrink-0 place-items-center rounded-lg bg-primary/10 text-sm font-semibold text-primary ${className}`}>{name.slice(0, 1).toUpperCase()}</span>
);

/** Sidebar header: shows the open application and jumps between applications. */
export function AppSwitcher() {
  const navigate = useNavigate();
  const { isMobile } = useSidebar();
  const { route, env, app } = useCurrentApp();
  const [open, setOpen] = useState(false);
  // Same key and request as the Applications page, so both share one cache entry.
  const apps = useQuery({ queryKey: ['apps'], queryFn: () => api<Page<App>>('/admin/v1/apps', { query: { limit: 200 } }) });

  const go = (to: string) => { setOpen(false); navigate(to); };

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <SidebarMenuButton size="lg" className="data-[state=open]:bg-sidebar-accent" aria-label="Switch application">
              {route ? (
                <>
                  <AppMark name={app?.name ?? '?'} logo={app?.logo} />
                  <span className="grid flex-1 text-left leading-tight">
                    <span className="truncate font-semibold">{app?.name ?? 'Loading…'}</span>
                    <span className="truncate text-xs text-muted-foreground capitalize">{env}</span>
                  </span>
                </>
              ) : (
                <>
                  <span className="grid size-8 shrink-0 place-items-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground"><Bell className="size-4" /></span>
                  <span className="grid flex-1 text-left leading-tight">
                    <span className="truncate font-semibold">{APP_NAME}</span>
                    <span className="truncate text-xs text-muted-foreground">All applications</span>
                  </span>
                </>
              )}
              <ChevronsUpDown className="ml-auto" />
            </SidebarMenuButton>
          </PopoverTrigger>
          <PopoverContent className="w-72 p-0" side={isMobile ? 'bottom' : 'right'} align="start" sideOffset={8}>
            <Command>
              <CommandInput placeholder="Search applications…" />
              <CommandList>
                <CommandEmpty>No applications found.</CommandEmpty>
                <CommandGroup heading="Applications">
                  {apps.data?.data.map((a) => (
                    <CommandItem key={a.id} value={`${a.name} ${a.slug} ${a.id}`} onSelect={() => go(`/apps/${a.id}?env=${env}`)}>
                      <AppMark name={a.name} logo={a.logo} className="size-6 text-xs" />
                      <span className="grid min-w-0 flex-1 leading-tight">
                        <span className="truncate">{a.name}</span>
                        <span className="truncate font-mono text-[11px] text-muted-foreground">{a.slug}</span>
                      </span>
                      {route?.appId === a.id && <Check className="ml-auto" />}
                    </CommandItem>
                  ))}
                </CommandGroup>
                <CommandSeparator />
                <CommandGroup>
                  <CommandItem forceMount value="__all" onSelect={() => go('/')}><LayoutGrid /> All applications</CommandItem>
                  <CommandItem forceMount value="__new" onSelect={() => go('/apps/new')}><Plus /> New application</CommandItem>
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
